import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import PrintIcon from '@mui/icons-material/Print';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PersonIcon from '@mui/icons-material/Person';
import RefreshIcon from '@mui/icons-material/Refresh';

interface WaiterAction {
  action: string;
  waiter: string;
  time: string;
  details: {
    tableName?: string;
    tableId?: string;
    openedAt?: string;
    productId?: string;
    productName?: string;
    quantity?: number;
    unitPrice?: number;
    sqlItemId?: number;
    [key: string]: any;
  };
}

interface WaiterActionsHistoryModalProps {
  open: boolean;
  onClose: () => void;
  waiterActionsJson?: string | null;
  tableName?: string;
  tableId?: string; // SQL'den güncel veri çekmek için
}

const WaiterActionsHistoryModal: React.FC<WaiterActionsHistoryModalProps> = ({
  open,
  onClose,
  waiterActionsJson,
  tableName,
  tableId,
}) => {
  const [actions, setActions] = useState<WaiterAction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // SQL'den güncel verileri çek
  const loadActionsFromSQL = useCallback(async () => {
    if (!tableId) {
      // tableId yoksa prop'tan gelen JSON'u kullan
      if (waiterActionsJson) {
        try {
          const parsed = JSON.parse(waiterActionsJson);
          setActions(Array.isArray(parsed) ? parsed : []);
        } catch (error) {
          console.error('WaiterActionsJson parse hatası:', error);
          setActions([]);
        }
      } else {
        setActions([]);
      }
      return;
    }
    
    setIsLoading(true);
    try {
      const { restaurantActiveSaleApi } = await import('../../../../../services/restaurantApiService');
      const activeSale = await restaurantActiveSaleApi.getByTableId(parseInt(tableId));
      
      if (activeSale?.WaiterActionsJson) {
        const parsed = JSON.parse(activeSale.WaiterActionsJson);
        setActions(Array.isArray(parsed) ? parsed : []);
        console.log('📜 [MASA GEÇMİŞİ] SQL\'den yüklendi:', parsed.length, 'işlem');
      } else {
        setActions([]);
      }
    } catch (error) {
      console.error('Masa geçmişi yüklenirken hata:', error);
      // Hata durumunda prop'tan gelen JSON'u kullan
      if (waiterActionsJson) {
        try {
          const parsed = JSON.parse(waiterActionsJson);
          setActions(Array.isArray(parsed) ? parsed : []);
        } catch {
          setActions([]);
        }
      } else {
        setActions([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, [tableId, waiterActionsJson]);
  
  // Modal açıldığında verileri yükle
  useEffect(() => {
    if (open) {
      loadActionsFromSQL();
    }
  }, [open, loadActionsFromSQL]);

  // Action tipine göre Türkçe açıklama ve renk
  const getActionInfo = (action: string) => {
    switch (action) {
      case 'open_table':
        return { label: 'Masa Açıldı', color: 'success' as const, icon: <TableRestaurantIcon fontSize="small" /> };
      case 'add_item':
        return { label: 'Ürün Eklendi', color: 'primary' as const, icon: <AddCircleIcon fontSize="small" /> };
      case 'remove_item':
        return { label: 'Ürün Silindi', color: 'error' as const, icon: <RemoveCircleIcon fontSize="small" /> };
      case 'print_kitchen':
        return { label: 'Mutfağa Yazdırıldı', color: 'warning' as const, icon: <PrintIcon fontSize="small" /> };
      case 'print_receipt':
        return { label: 'Adisyon Yazdırıldı', color: 'info' as const, icon: <ReceiptIcon fontSize="small" /> };
      case 'close_table':
        return { label: 'Masa Kapatıldı', color: 'default' as const, icon: <TableRestaurantIcon fontSize="small" /> };
      default:
        return { label: action, color: 'default' as const, icon: <HistoryIcon fontSize="small" /> };
    }
  };

  // Tarihi formatla
  const formatDateTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  // Detay açıklaması oluştur
  const getDetailDescription = (action: WaiterAction): string => {
    const { details } = action;
    switch (action.action) {
      case 'open_table':
        return `${details.tableName || 'Masa'} açıldı`;
      case 'add_item':
        return `${details.productName || 'Ürün'} x ${details.quantity || 1} - ${details.unitPrice?.toFixed(2) || '0.00'} TL`;
      case 'remove_item':
        return `${details.productName || 'Ürün'} x ${details.quantity || 1} silindi`;
      case 'print_kitchen':
        return `${details.itemCount || 0} ürün mutfağa gönderildi`;
      case 'print_receipt':
        return `Adisyon yazdırıldı`;
      case 'close_table':
        return `Masa kapatıldı`;
      default:
        return JSON.stringify(details);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '80vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          py: 1.5,
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <HistoryIcon />
          <Typography variant="h6">
            Masa Geçmişi {tableName && `- ${tableName}`}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'inherit' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {isLoading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress size={50} sx={{ mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              Yükleniyor...
            </Typography>
          </Box>
        ) : actions.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <HistoryIcon sx={{ fontSize: 60, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Henüz bir işlem kaydı bulunmuyor
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} elevation={0} sx={{ maxHeight: '60vh' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>Saat</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>İşlem</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>Garson</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>Detay</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>Silme Nedeni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {actions.map((action, index) => {
                  const actionInfo = getActionInfo(action.action);
                  return (
                    <TableRow 
                      key={index} 
                      hover
                      sx={{ 
                        '&:nth-of-type(odd)': { bgcolor: 'grey.50' },
                      }}
                    >
                      <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
                        {formatDateTime(action.time)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={actionInfo.icon}
                          label={actionInfo.label}
                          color={actionInfo.color}
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <PersonIcon fontSize="small" sx={{ color: 'grey.500' }} />
                          <Typography variant="body2" fontWeight={500}>
                            {action.waiter || 'Bilinmiyor'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.85rem' }}>
                        {getDetailDescription(action)}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.85rem' }}>
                        {action.action === 'remove_item' && action.details?.reason ? (
                          <Typography variant="body2" color="error.main" fontWeight="medium">
                            {action.details.reason}
                          </Typography>
                        ) : action.action === 'remove_item' ? (
                          <Typography variant="body2" color="text.disabled">-</Typography>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
          Toplam {actions.length} işlem kaydı
        </Typography>
        <Button 
          variant="outlined" 
          startIcon={isLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={loadActionsFromSQL}
          disabled={isLoading}
          sx={{ mr: 1 }}
        >
          Yenile
        </Button>
        <Button variant="contained" onClick={onClose}>
          Kapat
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WaiterActionsHistoryModal;
